import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <ProfilePicker> — pure controlled picker for an org's `chatProfile`
 * (a profile-name reference in the resolver-unified notation).
 *
 * §P8 of `ai-docs/llm-resolver-unification-impl-plan.md`. Mirrors the
 * lexportal-side picker (`lexportal/src/components/pickers/ProfilePicker.tsx`)
 * — same wire format, same inline-mirror approach.
 *
 * Wire format: `<BASE>` or `<BASE>:<thinkingLevel>` where:
 *   - `BASE` ∈ {"MAX","HIGH","MED","LOW"} (platform's profile catalog)
 *   - `<thinkingLevel>` ∈ pi-ai's 5-level enum (resolver Slice 1):
 *       `minimal` | `low` | `medium` | `high` | `xhigh`, OR absent.
 *
 * The picker's two halves (base + thinking overlay) serialise to a
 * single composite string the lexserver PATCH endpoint validates against
 * `RESOLVABLE_PROFILE_NAME_REGEX` (P4 of the plan).
 *
 * Inline mirror: `BASE_PROFILES` + `THINKING_OVERLAY_OPTIONS` are
 * duplicated from `@lexiun/lx-llm-resolver` for browser-bundle safety
 * (the lexportal sibling does the same). Drift surfaces at the
 * lexserver PATCH gate.
 */
import { useOrgAdminT } from "../i18n/context.js";
const BASE_PROFILES = ["MAX", "HIGH", "MED", "LOW"];
const THINKING_OVERLAY_OPTIONS = [
    { value: "", label: "(profile default)" },
    { value: "minimal", label: "minimal" },
    { value: "low", label: "low" },
    { value: "medium", label: "medium" },
    { value: "high", label: "high" },
    { value: "xhigh", label: "xhigh" },
];
function parseWire(v) {
    if (!v)
        return { base: "", overlay: "" };
    const idx = v.indexOf(":");
    const baseRaw = idx === -1 ? v : v.slice(0, idx);
    const overlayRaw = idx === -1 ? "" : v.slice(idx + 1);
    const base = BASE_PROFILES.includes(baseRaw)
        ? baseRaw
        : "";
    const overlay = THINKING_OVERLAY_OPTIONS.some((o) => o.value === overlayRaw)
        ? overlayRaw
        : "";
    return { base, overlay };
}
function serialiseWire(base, overlay) {
    if (!base)
        return "";
    return overlay ? `${base}:${overlay}` : base;
}
export function ProfilePicker({ value, onChange, label, disabled }) {
    const t = useOrgAdminT();
    const parsed = parseWire(value);
    return (_jsxs("div", { className: "space-y-1", "data-testid": "profile-picker", children: [label && (_jsx("label", { className: "text-sm font-medium text-slate-700 dark:text-slate-300", children: label })), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("select", { "aria-label": t("profilePicker.ariaBaseProfile"), value: parsed.base, onChange: (e) => onChange(serialiseWire(e.target.value, parsed.overlay)), disabled: disabled, className: "rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800", children: [_jsx("option", { value: "", children: t("profilePicker.unset") }), BASE_PROFILES.map((b) => (_jsx("option", { value: b, children: b }, b)))] }), _jsx("span", { className: "text-sm text-slate-500", children: t("profilePicker.thinkingLabel") }), _jsx("select", { "aria-label": t("profilePicker.ariaThinkingOverlay"), value: parsed.overlay, onChange: (e) => onChange(serialiseWire(parsed.base, e.target.value)), disabled: disabled || !parsed.base, className: "rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800", children: THINKING_OVERLAY_OPTIONS.map((o) => (_jsx("option", { value: o.value, children: o.value === "" ? t("profilePicker.profileDefault") : o.label }, o.value))) })] })] }));
}
//# sourceMappingURL=ProfilePicker.js.map